<template>
  <div class="page exam-page">
    <xh-header />

    <div class="container-width" v-if="course">
      <div class="ex-bread">
        <span class="mouse" @click="back">
          <i class="el-icon-arrow-left"></i> 返回课程
        </span>
      </div>

      <!-- 答题阶段 -->
      <div v-if="!finished">
        <div class="ex-head">
          <div class="ex-title">
            <i class="el-icon-document-checked"></i>
            {{ course.courseName }} · 课程考试
          </div>
          <div class="ex-meta">
            <span>进度 <b>{{ currentIndex + 1 }} / {{ questions.length }}</b></span>
            <span>已答 <b :class="{ 'green': answeredCount === questions.length }">{{ answeredCount }}</b> / {{ questions.length }}</span>
            <span>用时 <b>{{ formatTime(elapsed) }}</b></span>
          </div>
          <div class="ex-progress">
            <el-progress
              :percentage="questions.length ? Math.round(answeredCount / questions.length * 100) : 0"
              :show-text="false"
              :stroke-width="8"
              color="#4c2ac9">
            </el-progress>
          </div>
        </div>

        <div class="rule-tip">
          <i class="el-icon-medal"></i>
          及格规则：{{ passRuleText }}
        </div>
        <div class="timer-tip">
          <i class="el-icon-time"></i>
          每题限时 <b>60 秒</b>：作答后可进入下一题，未作答到 60 秒将自动跳下一题；<b>不可返回上一题</b>。
        </div>

        <div v-if="!questions.length && loaded" class="empty-block">
          本课程暂未配置考试题目 <el-button size="small" @click="back">返回</el-button>
        </div>

        <!-- 单题卡片 -->
        <div class="q-card" v-if="currentQuestion">
          <div class="q-head">
            <span class="q-no">{{ currentIndex + 1 }}.</span>
            <span class="q-text">{{ currentQuestion.question }}</span>
            <el-tag size="mini" :type="currentQuestion.questionType === 2 ? 'warning' : 'info'">
              {{ currentQuestion.questionType === 2 ? '多选' : '单选' }}
            </el-tag>
          </div>

          <div class="q-img" v-if="currentQuestion.questionImg">
            <el-image
              :src="currentQuestion.questionImg"
              fit="contain"
              :preview-src-list="[currentQuestion.questionImg]">
              <div slot="error" class="img-err"><i class="el-icon-picture-outline"></i></div>
            </el-image>
          </div>

          <div class="q-options">
            <template v-if="currentQuestion.questionType === 2">
              <el-checkbox-group v-model="multiAnswers[currentQuestion.id]">
                <el-checkbox
                  v-for="opt in currentQuestion.options"
                  :key="opt.k"
                  :label="opt.k"
                  class="opt">
                  <span class="opt-key">{{ opt.k }}.</span>
                  <span class="opt-text">{{ opt.v }}</span>
                </el-checkbox>
              </el-checkbox-group>
            </template>
            <template v-else>
              <el-radio-group v-model="singleAnswers[currentQuestion.id]">
                <el-radio
                  v-for="opt in currentQuestion.options"
                  :key="opt.k"
                  :label="opt.k"
                  class="opt">
                  <span class="opt-key">{{ opt.k }}.</span>
                  <span class="opt-text">{{ opt.v }}</span>
                </el-radio>
              </el-radio-group>
            </template>
          </div>
        </div>

        <!-- 倒计时 / 已作答状态 -->
        <div class="status-bar" v-if="currentQuestion">
          <template v-if="currentAnswered">
            <span class="answered-tag"><i class="el-icon-circle-check"></i> 已作答，可进入下一题</span>
          </template>
          <template v-else>
            <span class="countdown" :class="{ 'urgent': countdown <= 10 }">
              <i class="el-icon-time"></i> 剩余 <b>{{ countdown }}</b> 秒
            </span>
          </template>
        </div>

        <div class="nav-area" v-if="questions.length">
          <span class="nav-hint"><i class="el-icon-lock"></i> 不可返回上一题</span>
          <el-button
            type="primary"
            size="medium"
            :loading="submitting"
            @click="nextClick">
            {{ isLast ? '交卷' : '下一题' }}
            <i class="el-icon-arrow-right"></i>
          </el-button>
        </div>
      </div>

      <!-- 结果阶段 -->
      <div v-else class="result-wrap">
        <div class="result-card">
          <div class="r-icon" :class="result.record.passFlag === 1 ? 'ok' : 'fail'">
            <i :class="result.record.passFlag === 1 ? 'el-icon-success' : 'el-icon-warning'"></i>
          </div>
          <div class="r-title">
            {{ result.record.passFlag === 1 ? '恭喜，考试通过' : '很遗憾，本次未通过' }}
          </div>
          <div class="r-stat">
            <div>
              <div class="rs-num">{{ result.record.score }}</div>
              <div class="rs-lab">得分</div>
            </div>
            <div>
              <div class="rs-num">{{ result.record.totalNum }}</div>
              <div class="rs-lab">题目数</div>
            </div>
            <div>
              <div class="rs-num">{{ formatTime(result.record.duration) }}</div>
              <div class="rs-lab">用时</div>
            </div>
          </div>
          <div class="r-rule">{{ passRuleText }}</div>
          <div class="r-actions">
            <el-button type="primary" @click="goRecord">查看作答记录</el-button>
            <el-button @click="retry" v-if="result.record.passFlag !== 1">重新答题</el-button>
            <el-button @click="back">返回课程</el-button>
          </div>
        </div>
      </div>
    </div>

    <xh-footer />
  </div>
</template>

<script>
import XhHeader from '../../../reveals/xh-header.vue'
import XhFooter from '../../../reveals/xh-footer.vue'
import {
  getStudyCourseDetail,
  getStudyExamQuestions,
  submitStudyExam
} from '@/service/apis/study'

// 每题作答时限（秒）
const PER_Q_SEC = 60

export default {
  name: 'StudyExam',
  components: { XhHeader, XhFooter },
  data () {
    return {
      courseId: null,
      account: null,
      course: null,
      questions: [],
      loaded: false,
      singleAnswers: {},  // {qid: 'A'}
      multiAnswers: {},   // {qid: ['A','B']}
      currentIndex: 0,
      questionStartTs: 0,
      countdown: PER_Q_SEC,
      examStartTs: 0,
      elapsed: 0,
      timer: null,
      submitting: false,
      finished: false,
      result: null  // { record, details }
    }
  },
  computed: {
    currentQuestion () {
      return this.questions[this.currentIndex] || null
    },
    currentAnswered () {
      const q = this.currentQuestion
      if (!q) return false
      if (q.questionType === 2) {
        return (this.multiAnswers[q.id] || []).length > 0
      }
      return !!this.singleAnswers[q.id]
    },
    isLast () {
      return this.questions.length > 0 && this.currentIndex >= this.questions.length - 1
    },
    answeredCount () {
      let n = 0
      this.questions.forEach(q => {
        if (q.questionType === 2) {
          if ((this.multiAnswers[q.id] || []).length > 0) n++
        } else {
          if (this.singleAnswers[q.id]) n++
        }
      })
      return n
    },
    reviewDetails () {
      const details = (this.result && Array.isArray(this.result.details)) ? this.result.details : []
      return details.slice().sort((a, b) => (a.sort || 0) - (b.sort || 0))
    },
    passRuleText () {
      if (this.course && this.course.passRightNum > 0) {
        return `答对题数 ≥ ${ this.course.passRightNum } 题视为通过`
      }
      return '得分 ≥ 60 分视为通过'
    }
  },
  created () {
    const cid = this.$route.query.courseId
    if (!cid) {
      this.$message.error('参数缺失')
      this.$router.replace('/userinfo')
      return
    }
    this.courseId = Number(cid)
    const acct = localStorage.getItem('ACCOUNT')
    if (!acct) {
      this.$router.replace('/login')
      return
    }
    this.account = JSON.parse(acct)
    this.startExam()
    this.loadAll()
  },
  beforeDestroy () {
    this.clearTimer()
  },
  methods: {
    clearTimer () {
      if (this.timer) {
        clearInterval(this.timer)
        this.timer = null
      }
    },
    // 开启/重置一场考试
    startExam () {
      this.examStartTs = Date.now()
      this.questionStartTs = Date.now()
      this.elapsed = 0
      this.countdown = PER_Q_SEC
      this.currentIndex = 0
      this.clearTimer()
      this.timer = setInterval(() => this.tick(), 1000)
    },
    tick () {
      if (this.finished || !this.questions.length) return
      const now = Date.now()
      this.elapsed = Math.floor((now - this.examStartTs) / 1000)
      // 已作答：冻结当前题倒计时，不自动跳题
      if (this.currentAnswered) return
      const remain = PER_Q_SEC - Math.floor((now - this.questionStartTs) / 1000)
      this.countdown = Math.max(0, remain)
      if (this.countdown <= 0) this.goNext(true)
    },
    formatTime (sec) {
      sec = Math.floor(Number(sec) || 0)
      const h = Math.floor(sec / 3600)
      const m = Math.floor((sec % 3600) / 60)
      const s = sec % 60
      const pad = n => String(n).padStart(2, '0')
      return h > 0 ? `${ pad(h) }:${ pad(m) }:${ pad(s) }` : `${ pad(m) }:${ pad(s) }`
    },
    handle9994 (res) {
      if (res && res.errCode === 9994) {
        localStorage.clear()
        this.$router.replace('/login')
        return true
      }
      return false
    },
    loadAll () {
      getStudyCourseDetail.call(this, { id: this.courseId }).then(res => {
        if (this.handle9994(res)) return
        if (res.errCode === 0) this.course = res.data
      })
      getStudyExamQuestions.call(this, {
        courseId: this.courseId,
        pageIndex: 1,
        pageSize: 1000
      }).then(res => {
        if (this.handle9994(res)) return
        if (res.errCode === 0 && res.data && Array.isArray(res.data.records)) {
          this.questions = res.data.records.map(q => ({
            ...q,
            options: this.buildOptions(q)
          }))
          // 初始化答案占位
          this.questions.forEach(q => {
            if (q.questionType === 2) {
              this.$set(this.multiAnswers, q.id, [])
            } else {
              this.$set(this.singleAnswers, q.id, '')
            }
          })
          // 进入第一题
          this.currentIndex = 0
          this.questionStartTs = Date.now()
          this.countdown = PER_Q_SEC
        }
        this.loaded = true
      })
    },
    buildOptions (q) {
      return ['A', 'B', 'C', 'D']
        .filter(k => q['option' + k])
        .map(k => ({ k, v: q['option' + k] }))
    },
    buildReviewOptions (d) {
      return ['A', 'B', 'C', 'D']
        .filter(k => d['option' + k])
        .map(k => ({ k, v: d['option' + k] }))
    },
    isInAnswer (answer, k) {
      if (!answer) return false
      return answer.toUpperCase().indexOf(k) >= 0
    },
    reviewOptClass (d, k) {
      if (this.isInAnswer(d.rightAnswer, k)) return 'opt-right'
      if (this.isInAnswer(d.userAnswer, k)) return 'opt-wrong'
      return ''
    },
    enterQuestion (idx) {
      this.currentIndex = idx
      this.questionStartTs = Date.now()
      this.countdown = PER_Q_SEC
      this.$nextTick(() => window.scrollTo({ top: 0, behavior: 'smooth' }))
    },
    goNext () {
      if (this.currentIndex >= this.questions.length - 1) {
        this.doSubmit()
        return
      }
      this.enterQuestion(this.currentIndex + 1)
    },
    nextClick () {
      if (this.currentAnswered) {
        // 已作答：末题确认交卷，否则直接下一题
        if (this.isLast) {
          this.confirmSubmit()
        } else {
          this.goNext()
        }
        return
      }
      // 未作答：温馨提示
      const msg = this.isLast
        ? '您本题尚未作答，确认提交交卷吗？（未答题按答错计）'
        : '您还未作答本题，确认跳过进入下一题吗？'
      this.$confirm(msg, '温馨提示', {
        confirmButtonText: this.isLast ? '确认交卷' : '确认跳过',
        cancelButtonText: '继续答题',
        type: 'warning'
      }).then(() => this.goNext()).catch(() => {})
    },
    confirmSubmit () {
      this.$confirm('答案提交后无法修改，确认交卷？', '提示', {
        confirmButtonText: '确认交卷',
        cancelButtonText: '再检查一下',
        type: 'info'
      }).then(() => this.doSubmit()).catch(() => {})
    },
    doSubmit () {
      if (this.submitting) return
      this.submitting = true
      const answers = this.questions.map(q => {
        let userAnswer = ''
        if (q.questionType === 2) {
          const arr = (this.multiAnswers[q.id] || []).slice().sort()
          userAnswer = arr.join('')
        } else {
          userAnswer = this.singleAnswers[q.id] || ''
        }
        return { questionId: q.id, userAnswer }
      })
      const examStartTime = this.formatDate(this.examStartTs, 'YYYY-mm-dd hh:ii:ss')
      submitStudyExam.call(this, {
        courseId: this.courseId,
        examStartTime,
        duration: this.elapsed,
        answers
      }).then(res => {
        this.submitting = false
        if (this.handle9994(res)) return
        if (res.errCode === 0 && res.data && res.data.record) {
          this.result = res.data  // { record, details }
          this.finished = true
          this.clearTimer()
          window.scrollTo({ top: 0 })
        } else if (res.errCode !== 0) {
          this.$message.error(res.errMsg || '提交失败，请重试')
        }
      }).catch(() => {
        this.submitting = false
      })
    },
    retry () {
      this.singleAnswers = {}
      this.multiAnswers = {}
      this.questions.forEach(q => {
        if (q.questionType === 2) {
          this.$set(this.multiAnswers, q.id, [])
        } else {
          this.$set(this.singleAnswers, q.id, '')
        }
      })
      this.finished = false
      this.result = null
      this.startExam()
      window.scrollTo(0, 0)
    },
    goRecord () {
      this.$router.push(`/study/exam-record?courseId=${ this.courseId }&id=${ this.result.record.id }`)
    },
    back () {
      this.$router.push('/study/course?id=' + this.courseId)
    }
  }
}
</script>

<style scoped>
.exam-page { background: #fff; }
.container-width { padding: 16px 0 60px; min-height: 60vh; }
.ex-bread {
  font-size: 13px;
  color: #888;
  padding: 6px 0 12px;
}
.ex-bread .mouse:hover { color: #4c2ac9; }
.ex-head {
  border: solid 1px #ece8ff;
  border-radius: 8px;
  padding: 16px 20px;
  margin-bottom: 12px;
  background: linear-gradient(135deg, #faf8ff 0%, #fff 60%);
}
.ex-title {
  font-size: 18px;
  font-weight: 600;
  color: #2e2e2e;
}
.ex-title i { color: #4c2ac9; margin-right: 6px; }
.ex-meta {
  margin-top: 10px;
  font-size: 13px;
  color: #666;
  display: flex;
  gap: 22px;
}
.ex-meta b { color: #4c2ac9; padding: 0 2px; }
.ex-meta b.green { color: #4caf50; }
.ex-progress { margin-top: 12px; }

.rule-tip {
  font-size: 13px;
  color: #b35c00;
  background: #fff7e6;
  padding: 8px 14px;
  border-radius: 4px;
  margin-bottom: 8px;
}
.rule-tip i { margin-right: 4px; }
.timer-tip {
  font-size: 13px;
  color: #555;
  background: #f8f6ff;
  padding: 8px 14px;
  border-radius: 4px;
  margin-bottom: 16px;
  line-height: 22px;
}
.timer-tip i { margin-right: 4px; color: #4c2ac9; }
.timer-tip b { color: #4c2ac9; }

/* 单题卡片 */
.q-card {
  border: solid 1px #f0f0f0;
  border-radius: 10px;
  padding: 22px 24px;
  margin-bottom: 14px;
}
.q-head {
  font-size: 16px;
  color: #2e2e2e;
  line-height: 26px;
  margin-bottom: 14px;
}
.q-no { color: #4c2ac9; font-weight: 600; margin-right: 6px; }
.q-head .el-tag { margin-left: 8px; }
.q-img {
  margin: 0 0 16px;
  max-width: 360px;
}
.q-img .el-image {
  width: 100%;
  max-height: 260px;
  border-radius: 6px;
  border: solid 1px #f0f0f0;
}
.q-img .img-err {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 80px;
  color: #ddd;
  font-size: 28px;
}
.q-options { padding-left: 8px; }
.q-options .opt {
  display: block;
  padding: 10px 12px;
  margin-bottom: 8px;
  border: solid 1px #f0f0f0;
  border-radius: 6px;
  white-space: normal;
  font-weight: normal;
  color: #555;
}
.q-options .opt:hover { border-color: #d9ccff; background: #faf8ff; }
.q-options .opt-key {
  color: #4c2ac9;
  font-weight: 600;
  margin-right: 6px;
}
.q-options .opt-text { color: #555; }

/* 状态条 */
.status-bar {
  text-align: center;
  margin-bottom: 14px;
}
.status-bar .countdown {
  font-size: 16px;
  color: #4c2ac9;
  font-weight: 500;
  background: #f3eeff;
  padding: 8px 18px;
  border-radius: 20px;
}
.status-bar .countdown b { font-size: 22px; padding: 0 2px; }
.status-bar .countdown.urgent { color: #f56c6c; background: #fff0f0; }
.status-bar .answered-tag {
  font-size: 15px;
  color: #4caf50;
  background: #ecfaf3;
  padding: 8px 18px;
  border-radius: 20px;
}
.status-bar .answered-tag i { margin-right: 4px; }

/* 导航区 */
.nav-area {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 8px 0 0;
}
.nav-hint {
  font-size: 12px;
  color: #bbb;
}
.nav-hint i { margin-right: 4px; }

.empty-block {
  text-align: center;
  color: #999;
  padding: 60px 20px;
  font-size: 14px;
}

/* 结果卡 */
.result-wrap { display: flex; flex-direction: column; gap: 18px; }
.result-card {
  border: solid 1px #ece8ff;
  border-radius: 12px;
  padding: 36px 24px 30px;
  text-align: center;
  background: linear-gradient(180deg, #faf8ff 0%, #fff 80%);
}
.r-icon { font-size: 64px; }
.r-icon.ok { color: #4caf50; }
.r-icon.fail { color: #f0a020; }
.r-title {
  font-size: 22px;
  color: #2e2e2e;
  font-weight: 600;
  margin: 12px 0 24px;
}
.r-stat {
  display: flex;
  justify-content: center;
  margin-bottom: 18px;
}
.r-stat > div {
  flex: 0 1 110px;
  text-align: center;
  border-right: solid 1px #f0ebff;
  padding: 0 6px;
}
.r-stat > div:last-child { border-right: none; }
.rs-num {
  font-size: 24px;
  color: #4c2ac9;
  font-weight: 600;
  line-height: 32px;
}
.rs-lab { font-size: 12px; color: #999; }
.r-rule {
  font-size: 13px;
  color: #888;
  margin-bottom: 22px;
}
.r-actions .el-button { margin: 0 4px; }

/* 答题解析 */
.review-block {
  border: solid 1px #ece8ff;
  border-radius: 10px;
  overflow: hidden;
}
.review-title {
  font-size: 16px;
  color: #2e2e2e;
  font-weight: 600;
  padding: 14px 20px;
  background: #faf8ff;
  border-bottom: solid 1px #f0ebff;
}
.review-title i { color: #4c2ac9; margin-right: 6px; }
.review-item {
  padding: 18px 22px;
  border-bottom: solid 1px #f5f5f5;
}
.review-item:last-child { border-bottom: none; }
.rv-head {
  font-size: 15px;
  color: #2e2e2e;
  line-height: 24px;
  margin-bottom: 12px;
}
.rv-no { color: #4c2ac9; font-weight: 600; margin-right: 6px; }
.rv-head .el-tag { margin-left: 8px; }
.rv-head .rv-flag { margin-left: 4px; }
.rv-img {
  margin: 0 0 14px;
  max-width: 360px;
}
.rv-img .el-image {
  width: 100%;
  max-height: 260px;
  border-radius: 6px;
  border: solid 1px #f0f0f0;
}
.rv-img .img-err {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 80px;
  color: #ddd;
  font-size: 28px;
}
.rv-opts { padding-left: 0; }
.rv-opt {
  padding: 8px 12px;
  border-radius: 6px;
  margin-bottom: 6px;
  font-size: 14px;
  color: #555;
  background: #fafafa;
  border: solid 1px #f0f0f0;
  display: flex;
  align-items: center;
}
.rv-opt-key { color: #888; font-weight: 600; margin-right: 8px; }
.rv-opt-text { flex: 1; }
.rv-opt.opt-right {
  background: #ecfaf3;
  border-color: #b7ebc4;
}
.rv-opt.opt-right .rv-opt-key { color: #4caf50; }
.rv-opt.opt-wrong {
  background: #fff0f0;
  border-color: #fbc4c4;
}
.rv-opt.opt-wrong .rv-opt-key { color: #f56c6c; }
.rv-opt-flag {
  font-size: 12px;
  margin-left: 8px;
  padding: 1px 8px;
  border-radius: 10px;
}
.rv-opt-flag.right { color: #4caf50; background: rgba(76, 175, 80, .12); }
.rv-opt-flag.mine { color: #f56c6c; background: rgba(245, 108, 108, .12); }
.rv-summary {
  margin-top: 10px;
  padding: 8px 12px;
  font-size: 13px;
  color: #666;
  background: #fafafa;
  border-radius: 4px;
}
.rv-summary .sep { margin: 0 10px; color: #ddd; }
.rv-summary .green { color: #4caf50; }
.rv-summary .red { color: #f56c6c; }

.green { color: #4caf50; }
.red { color: #f56c6c; }

@media screen and (max-width: 800px) {
  .container-width { padding: 8px 16px 30px; }
  .ex-head { padding: 14px 14px; }
  .ex-title { font-size: 16px; }
  .ex-meta { font-size: 12px; gap: 14px; flex-wrap: wrap; }
  .q-card { padding: 16px 14px; }
  .q-head { font-size: 14px; }
  .q-img, .rv-img { max-width: 100%; }
  .r-title { font-size: 18px; }
  .r-stat { flex-wrap: wrap; }
  .r-stat > div {
    flex: 0 0 33.33%;
    border-right: none;
    padding: 6px 0;
  }
  .rs-num { font-size: 18px; }
  .r-actions .el-button { margin: 4px; }
  .review-item { padding: 14px 14px; }
  .rv-head { font-size: 14px; }
}
</style>
